import { useCallback, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import ReportPreview from "../../components/reporting-v2/ReportPreview";
import ReportActions from "../../components/reporting-v2/ReportActions";
import { useMetabaseLocation } from "../../components/reporting-v2/hooks/useMetabaseLocation";
import {
  useReportingDashboardQuery,
  useSavedReportsQuery,
  useCreateSavedReportMutation,
  useDeleteSavedReportMutation,
} from "../../graphql/generated";

export default function ReportingPage() {
  const { t } = useTranslation("reporting");

  const { data, isLoading, error } = useReportingDashboardQuery();
  const report = data?.reportingDashboard;

  // Current filter values and tab, read from Metabase via postMessage.
  const location = useMetabaseLocation();

  const { data: savedReportsData, refetch: refetchSaved } =
    useSavedReportsQuery();
  const savedReports = useMemo(
    () => savedReportsData?.savedReports ?? [],
    [savedReportsData],
  );
  const createSavedReport = useCreateSavedReportMutation();
  const deleteSavedReport = useDeleteSavedReportMutation();

  // Filter values applied to the embed when a saved report is opened.
  const [replay, setReplay] = useState<{
    params: Record<string, string>;
    tab: string | null;
  } | null>(null);
  const [selectedSavedReportId, setSelectedSavedReportId] = useState<
    string | null
  >(null);

  const handleSave = useCallback(
    (name: string) => {
      if (!location) return;
      createSavedReport.mutate(
        {
          input: {
            name,
            reportKey: location.tab ?? "reporting",
            filters: location.params,
          },
        },
        { onSuccess: () => refetchSaved() },
      );
    },
    [location, createSavedReport, refetchSaved],
  );

  const handleSelectSavedReport = useCallback(
    (id: string) => {
      const saved = savedReports.find((r) => r._id === id);
      if (!saved) return;
      setSelectedSavedReportId(id);
      setReplay({
        params: (saved.filters ?? {}) as Record<string, string>,
        tab: saved.reportKey || null,
      });
    },
    [savedReports],
  );

  const handleClearSavedReport = useCallback(() => {
    setSelectedSavedReportId(null);
    setReplay(null);
  }, []);

  const handleDeleteSavedReport = useCallback(
    (id: string) => {
      deleteSavedReport.mutate(
        { id },
        {
          onSuccess: () => {
            if (selectedSavedReportId === id) {
              setSelectedSavedReportId(null);
              setReplay(null);
            }
            refetchSaved();
          },
        },
      );
    },
    [deleteSavedReport, selectedSavedReportId, refetchSaved],
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {t("errors.loadList")}
      </Alert>
    );
  }

  if (!report) {
    return (
      <Alert severity="info" sx={{ m: 3 }}>
        {t("errors.noReports")}
      </Alert>
    );
  }

  return (
    <Stack
      sx={{
        height: "100%",
        m: -1,
        mt: -3,
        overflow: "hidden",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          py: 1,
        }}
      >
        <ReportActions
          savedReports={savedReports}
          selectedSavedReportId={selectedSavedReportId}
          canSave={!!location && Object.keys(location.params).length > 0}
          saving={createSavedReport.isLoading}
          onSave={handleSave}
          onSelectSavedReport={handleSelectSavedReport}
          onClearSavedReport={handleClearSavedReport}
          onDeleteSavedReport={handleDeleteSavedReport}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
        }}
      >
        <ReportPreview
          reportLabel={report.label}
          params={replay?.params}
          tab={replay?.tab}
        />
      </Box>
    </Stack>
  );
}
